import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import reportService from '../../services/reportService';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import { useCanMutate, isLimitedReadOnlyUser } from '../../hooks/useCanMutate';
import { authService } from '../../services/authService';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import { formatGuideSaveMessage, buildRecalculateRunProgressMessage, recalculateRunToastType } from '../../utils/kardexRecalculationHelpers';
import notificationService from '../../services/notificationService';

const eventLabels = {
  compra: 'Compra',
  venta: 'Venta',
  saldo_inicial: 'Saldo Inicial',
  traspaso: 'Traspaso',
  recepcion: 'Recepción',
  transferencia_gratuita: 'Transferencia Gratuita',
  descuento_obtenido: 'Descuento Obtenido',
  descuento_concedido: 'Descuento Concedido',
  descuento_con_mercaderia: 'Descuento con Mercadería',
  consumo: 'Consumo',
  devolucion: 'Devolución',
  salida_produccion: 'Salida a producción',
  entrada_produccion: 'Entrada de producción',
  entrada_producto_terminado: 'Entrada producto terminado',
};

const formatMoney4 = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '-';
  return `S/ ${Number(n).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
};

const pairsFromInconsistencyReport = (rows) => (rows || []).map((item) => ({
  product_id: item.product.id,
  warehouse_id: item.warehouse.id,
}));

const sortInconsistencyRows = (rows) => [...rows].sort((a, b) => {
  const nameCmp = (a.product?.name || '').localeCompare(b.product?.name || '');
  if (nameCmp !== 0) return nameCmp;
  return (a.warehouse?.code || '').localeCompare(b.warehouse?.code || '');
});

const mergeInconsistencyRows = (existing, incoming) => {
  const map = new Map();
  [...existing, ...incoming].forEach((row) => {
    const key = `${row.product.id}-${row.warehouse.id}`;
    map.set(key, row);
  });
  return sortInconsistencyRows(Array.from(map.values()));
};

const KardexInconsistencies = () => {
  const canMutate = useCanMutate();
  const isLimited = isLimitedReadOnlyUser(authService.getCurrentUser());
  const [data, setData] = useState([]);
  const [countFrom, setCountFrom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({
    isOpen: false,
    message: '',
    type: 'info',
    progress: null,
    persistent: false,
    duration: 5000,
  });
  const [expandedId, setExpandedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ scanned: 0, total: 0, percent: 0 });
  const [foundPairsCount, setFoundPairsCount] = useState(0);
  const hasLoaded = useRef(false);
  const scanCancelRef = useRef(false);
  const recalcPollRef = useRef(null);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadReport();
    }
  }, []);

  useEffect(() => () => {
    if (recalcPollRef.current) {
      clearTimeout(recalcPollRef.current);
    }
  }, []);

  const pollRecalculateRun = (runId, pairsTotal) => {
    const poll = async () => {
      try {
        const res = await notificationService.getKardexRecalculateRun(runId);
        if (!res?.success || !res.data) {
          return;
        }

        const run = res.data;
        const progress = run.progress_percent ?? (run.is_active ? 10 : 100);

        if (run.is_active) {
          setToast({
            isOpen: true,
            type: 'info',
            persistent: true,
            progress,
            duration: 5000,
            message: buildRecalculateRunProgressMessage(run, pairsTotal),
          });
          recalcPollRef.current = setTimeout(poll, 1200);
          return;
        }

        setToast({
          isOpen: true,
          type: recalculateRunToastType(run),
          persistent: false,
          progress: 100,
          duration: 8000,
          message: buildRecalculateRunProgressMessage(run, pairsTotal),
        });
        await loadReport();
      } catch (error) {
        console.error('Error al consultar progreso de recálculo:', error);
        setToast({
          isOpen: true,
          type: 'warning',
          persistent: false,
          progress: null,
          duration: 7000,
          message: 'Recálculo en segundo plano. Revise notificaciones para el resultado.',
        });
      }
    };

    setToast({
      isOpen: true,
      type: 'info',
      persistent: true,
      progress: 5,
      duration: 5000,
      message: `Recálculo en cola… ${pairsTotal} par(es)`,
    });

    recalcPollRef.current = setTimeout(poll, 800);
  };

  const loadReport = async () => {
    scanCancelRef.current = false;
    setScanning(true);
    setLoading(true);
    setData([]);
    setFoundPairsCount(0);
    setScanProgress({ scanned: 0, total: 0, percent: 0 });

    try {
      const meta = await reportService.getKardexInconsistenciesScanMeta();
      if (!meta.success) {
        setToast({ isOpen: true, message: meta.message || 'Error al iniciar escaneo', type: 'error' });
        return;
      }

      setCountFrom(meta.count_from || null);
      const totalPairs = meta.total_pairs || 0;
      const batchSize = meta.default_batch_size || 200;
      setScanProgress({ scanned: 0, total: totalPairs, percent: 0 });

      if (totalPairs === 0) {
        setData([]);
        return;
      }

      let offset = 0;
      let accumulated = [];

      while (offset < totalPairs) {
        if (scanCancelRef.current) {
          break;
        }

        const batch = await reportService.scanKardexInconsistenciesBatch({
          offset,
          batchSize,
          totalPairs,
        });

        if (!batch.success) {
          setToast({ isOpen: true, message: batch.message || 'Error durante el escaneo', type: 'error' });
          break;
        }

        const batchRows = batch.data || [];
        if (batchRows.length > 0) {
          accumulated = mergeInconsistencyRows(accumulated, batchRows);
          setData(accumulated);
          setFoundPairsCount(accumulated.length);
        }

        const progress = batch.progress || {};
        setScanProgress({
          scanned: progress.scanned ?? offset,
          total: progress.total_pairs ?? totalPairs,
          percent: progress.percent ?? 0,
        });

        if (progress.done) {
          break;
        }

        offset = progress.scanned ?? (offset + batchSize);
      }
    } catch (error) {
      console.error('Error al cargar inconsistencias:', error);
      setToast({ isOpen: true, message: 'Error al escanear inconsistencias de kardex', type: 'error' });
    } finally {
      setLoading(false);
      setScanning(false);
    }
  };

  const cancelScan = () => {
    scanCancelRef.current = true;
    setScanning(false);
    setLoading(false);
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const loadPreview = async () => {
    if (data.length === 0) {
      setToast({ isOpen: true, message: 'No hay pares con inconsistencias para recalcular.', type: 'info' });
      return;
    }

    setLoadingPreview(true);
    try {
      const pairs = pairsFromInconsistencyReport(data);
      const res = await reportService.previewRecalculateAllKardex({ pairs });
      if (res.success) {
        setPreview(res.data);
        setConfirmBulkOpen(true);
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo cargar la vista previa', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al preparar recálculo masivo',
        type: 'error',
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const executeBulkRecalculate = async () => {
    setBulkRunning(true);
    try {
      const pairs = pairsFromInconsistencyReport(data);
      const res = await reportService.recalculateAllKardex({ pairs });
      if (res.success) {
        setConfirmBulkOpen(false);
        setPreview(null);
        const d = res.data || {};
        const queued = res.kardex_recalculation?.status === 'queued';
        const pairsTotal = d.pairs_total ?? preview?.processed_pairs ?? pairs.length ?? 0;
        const runId = res.kardex_recalculation?.run_id;

        if (queued && runId) {
          pollRecalculateRun(runId, pairsTotal);
        } else {
          setToast({
            isOpen: true,
            message: formatGuideSaveMessage(
              res.message ||
                `Procesados: ${d.processed_pairs ?? 0}. Omitidos: ${d.skipped_pairs ?? 0}. Movimientos: ${d.updated_movements ?? 0}.`,
              res
            ),
            type: 'success',
            progress: null,
            persistent: false,
            duration: 6000,
          });
          await loadReport();
        }
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo recalcular', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al recalcular kardex masivamente',
        type: 'error',
      });
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link
          to="/reports/kardex"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-teal-700 mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver a Reportes
        </Link>
        <h1 className="text-2xl font-bold text-gray-800">Inconsistencias de Kardex</h1>
        <p className="text-gray-600 mt-1 max-w-3xl">
          Productos y almacenes con movimientos que presentan inconsistencias (costo unitario incorrecto, costo de recepción, stock o valor negativo).
        </p>
        {countFrom?.enabled && countFrom?.date ? (
          <div className="mt-3 max-w-3xl rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            Contando inconsistencias desde{' '}
            <strong>
              {formatDateLocaleLima(countFrom.date)}
              {countFrom.time ? ` ${String(countFrom.time).slice(0, 5)}` : ''}
            </strong>
            . Los movimientos anteriores no se reportan como inconsistencia.
          </div>
        ) : null}
        {canMutate ? (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={loadPreview}
              disabled={loadingPreview || bulkRunning || scanning || data.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
            >
              {loadingPreview ? 'Preparando…' : 'Recalcular todos (excepto stock negativo)'}
            </button>
            <button
              type="button"
              onClick={loadReport}
              disabled={scanning || bulkRunning}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 rounded-md text-sm font-medium"
            >
              {scanning ? 'Escaneando…' : 'Volver a escanear'}
            </button>
            {scanning ? (
              <button
                type="button"
                onClick={cancelScan}
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 text-red-700 hover:bg-red-50 rounded-md text-sm font-medium"
              >
                Detener escaneo
              </button>
            ) : null}
            <p className="text-xs text-gray-500 mt-2">
              {isLimited
                ? 'Solo se recalculan los productos con inconsistencias en sus almacenes asignados. Los pares con stock negativo se omiten aquí; use recálculo unitario desde «Ir al kardex».'
                : 'Corrige costos de salidas, recepciones, devoluciones y ajustes. Los pares con stock negativo no entran al masivo: abra el kardex del producto y use recálculo unitario.'}
            </p>
          </div>
        ) : null}
      </div>

      {(scanning || (loading && scanProgress.total > 0)) ? (
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-teal-200 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Escaneando kardex…</h2>
              <p className="text-sm text-gray-600 mt-1">
                Revisando productos y almacenes para detectar inconsistencias de costo y stock.
              </p>
            </div>
            <div className="text-right text-sm text-gray-700">
              <div className="font-semibold text-teal-700">{scanProgress.percent}%</div>
              <div>
                {scanProgress.scanned.toLocaleString('es-PE')} / {scanProgress.total.toLocaleString('es-PE')} pares
              </div>
            </div>
          </div>
          <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-teal-600 transition-all duration-300 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, scanProgress.percent))}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Inconsistencias encontradas hasta ahora: <strong>{foundPairsCount}</strong>
            {foundPairsCount > 0 ? ' — puede expandir filas mientras continúa el escaneo.' : ''}
          </p>
        </div>
      ) : null}

      {loading && scanProgress.total === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex justify-center">
            <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-center text-gray-500 mt-4">Preparando escaneo…</p>
        </div>
      ) : !scanning && !loading && data.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600 mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800">No hay inconsistencias</h2>
          <p className="text-gray-600 mt-2">No se encontraron productos con inconsistencias en el kardex.</p>
        </div>
      ) : data.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">{data.length}</span> producto(s)-almacén con al menos una inconsistencia.
              {scanning ? (
                <span className="text-teal-700"> Escaneo en curso ({scanProgress.percent}%).</span>
              ) : null}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Almacén
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nº inconsistencias
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item) => {
                  const rowId = `${item.product.id}-${item.warehouse.id}`;
                  const isExpanded = expandedId === rowId;
                  return (
                    <React.Fragment key={rowId}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {item.product.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {item.product.sku || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.warehouse.code} – {item.warehouse.name}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            {item.inconsistencies_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleExpand(rowId)}
                              className="text-teal-600 hover:text-teal-800 font-medium"
                            >
                              {isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                            </button>
                            <span className="text-gray-300">|</span>
                            <Link
                              to={`/reports/kardex/${item.product.id}/${item.warehouse.id}`}
                              className="text-teal-600 hover:text-teal-800 font-medium"
                            >
                              Ir al kardex
                            </Link>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-amber-50/50">
                          <td colSpan="5" className="px-4 py-3">
                            <div className="text-sm">
                              <p className="font-medium text-gray-700 mb-2">Movimientos con inconsistencia:</p>
                              <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                                <table className="min-w-full text-xs">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="px-3 py-2 text-left">Fecha</th>
                                      <th className="px-3 py-2 text-left">Hora</th>
                                      <th className="px-3 py-2 text-left">Evento</th>
                                      <th className="px-3 py-2 text-left">Guía</th>
                                      <th className="px-3 py-2 text-left">Referencia</th>
                                      <th className="px-3 py-2 text-right">Costo recepción</th>
                                      <th className="px-3 py-2 text-right">Costo traspaso</th>
                                      <th className="px-3 py-2 text-left">Inconsistencia</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.movements.map((mov) => (
                                      <tr key={mov.id} className="border-t border-gray-100">
                                        <td className="px-3 py-2">{formatDateLocaleLima(mov.movement_date)}</td>
                                        <td className="px-3 py-2">{mov.movement_time || '-'}</td>
                                        <td className="px-3 py-2">{eventLabels[mov.kardex_event] || mov.kardex_event}</td>
                                        <td className="px-3 py-2">{mov.entry_guide_code || mov.guide_number || '-'}</td>
                                        <td className="px-3 py-2">{mov.reference || '-'}</td>
                                        <td className="px-3 py-2 text-right">{formatMoney4(mov.reception_unit_price)}</td>
                                        <td className="px-3 py-2 text-right">{formatMoney4(mov.transfer_unit_price)}</td>
                                        <td className="px-3 py-2 text-amber-800 font-medium">{mov.revision}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        progress={toast.progress}
        persistent={toast.persistent}
        duration={toast.duration}
        onClose={() => setToast((prev) => ({ ...prev, isOpen: false, persistent: false, progress: null }))}
      />

      <ConfirmModal
        isOpen={confirmBulkOpen}
        onClose={() => {
          if (!bulkRunning) {
            setConfirmBulkOpen(false);
            setPreview(null);
          }
        }}
        onConfirm={executeBulkRecalculate}
        title="Recalcular todos los kardex"
        message={
          preview ? (
            <div className="text-sm text-gray-700 space-y-2 text-left">
              <p>
                Se recalcularán los pares producto-almacén <strong>sin stock negativo</strong>. Los que tienen stock negativo deben corregirse con <strong>recálculo unitario</strong> (Ir al kardex → Recalcular).
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Pares con cambios a aplicar: <strong>{preview.processed_pairs ?? 0}</strong></li>
                <li>Sin cambios necesarios: <strong>{preview.pairs_without_changes ?? 0}</strong></li>
                <li>Omitidos (stock negativo): <strong>{preview.skipped_pairs ?? 0}</strong></li>
                <li>Movimientos a corregir (estimado): <strong>{preview.updated_movements ?? 0}</strong></li>
              </ul>
                {(preview.skipped || []).length > 0 ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Los omitidos por stock negativo: abra ese producto-almacén y use recálculo unitario en el detalle del kardex.
                  {preview.skipped_truncated ? ' (Se muestran solo los primeros casos omitidos.)' : ''}
                </p>
              ) : null}
              <p className="text-xs text-gray-600">
                El recálculo se ejecutará en segundo plano; recibirá una notificación al finalizar.
              </p>
              <p>¿Desea continuar?</p>
            </div>
          ) : (
            '¿Desea recalcular todos los kardex elegibles?'
          )
        }
        confirmText={bulkRunning ? 'Procesando…' : 'Sí, recalcular'}
        cancelText="Cancelar"
      />
    </div>
  );
};

export default KardexInconsistencies;
